<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :width="isCreate ? '520px' : '640px'"
    append-to-body
    :close-on-click-modal="false"
    @open="loadDetail"
  >
    <!-- 新建人员组 -->
    <el-form v-if="isCreate" label-width="90px" size="small">
      <el-form-item label="所属岗位" required>
        <el-select v-model="form.stationName" placeholder="请选择岗位" style="width: 100%">
          <el-option v-for="s in stationOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="组名称" required>
        <el-input v-model="form.groupName" placeholder="例如：华东销售组" />
      </el-form-item>
      <el-form-item label="成员">
        <el-select
          v-model="selectedUserIds"
          multiple
          filterable
          remote
          :remote-method="searchUsers"
          :loading="userLoading"
          placeholder="输入姓名搜索并选择成员"
          style="width: 100%"
        >
          <el-option v-for="u in userOptions" :key="u.userId" :label="u.userName" :value="u.userId" />
        </el-select>
      </el-form-item>
    </el-form>

    <!-- 管理成员：组信息只读，成员列表可增删 -->
    <div v-else class="manage-members">
      <div class="group-info">
        <span class="info-item"><label>组名称</label>{{ form.groupName }}</span>
        <span class="info-item"><label>所属岗位</label>{{ form.stationName }}</span>
      </div>
      <div class="member-toolbar">
        <cargo-advance-select
          style="width: 260px"
          filterable
          clearable
          :select-type="selectTypeNames.basic_data_User"
          :default-value="addUserId"
          :callback-params="{ key: 'userId' }"
          @parentCallBack="onAddUserSelected"
        />
        <el-button type="primary" plain icon="el-icon-plus" :disabled="!pendingAddUser.userId" @click="addMember">
          添加成员
        </el-button>
      </div>
      <el-table :data="memberList" border size="small" max-height="320" empty-text="暂无成员，请搜索并添加">
        <el-table-column prop="userName" label="姓名" min-width="140" />
        <el-table-column prop="userId" label="用户ID" width="120" />
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" style="color:#F56C6C" @click="removeMember(scope.$index)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handleSubmit">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { savePersonGroup, getPersonGroupDetail } from '@/api/provision/provisionRule'
import { getDropdownUser } from '@/api/user'
import cargoAdvanceSelect from '@/components/Cargo/Common/cargoAdvanceSelect'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'

export default {
  name: 'GroupEditDialog',
  components: { cargoAdvanceSelect },
  props: {
    visible: { type: Boolean, default: false },
    groupUuid: { type: String, default: '' },
    groupName: { type: String, default: '' },
    groupStation: { type: String, default: '' },
    initialMembers: { type: Array, default: () => [] }
  },
  data() {
    return {
      stationOptions: ProvisionConstants.CONFIGURABLE_STATIONS,
      selectTypeNames: TYPE_NAMES,
      form: { groupName: '', stationName: '' },
      selectedUserIds: [],
      memberList: [],
      userOptions: [],
      userLoading: false,
      addUserId: null,
      pendingAddUser: { userId: '', userName: '' }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    isCreate() {
      return !this.groupUuid
    },
    dialogTitle() {
      if (this.isCreate) return '新建人员组'
      return this.form.groupName ? `管理成员 - ${this.form.groupName}` : '管理成员'
    }
  },
  mounted() {
    if (!this.isCreate) {
      this.form.groupName = this.groupName || ''
      this.form.stationName = this.groupStation || ''
      this.memberList = (this.initialMembers || []).map(m => ({
        userId: String(m.userId),
        userName: m.userName || m.userId
      }))
    }
    this.loadDetail()
  },
  methods: {
    async loadDetail() {
      if (!this.groupUuid) {
        this.form = { groupName: '', stationName: '' }
      } else {
        if (!this.form.groupName) this.form.groupName = this.groupName || ''
        if (!this.form.stationName) this.form.stationName = this.groupStation || ''
      }
      this.selectedUserIds = []
      this.userOptions = []
      this.addUserId = null
      this.pendingAddUser = { userId: '', userName: '' }
      if (!this.groupUuid) {
        this.memberList = []
        return
      }
      try {
        const res = await getPersonGroupDetail(this.groupUuid)
        if (res.ok && res.data) {
          if (res.data.group) {
            if (res.data.group.groupName) this.form.groupName = res.data.group.groupName
            if (res.data.group.stationName) this.form.stationName = res.data.group.stationName
          }
          const members = res.data.members || []
          if (members.length > 0) {
            this.memberList = members.map(m => ({
              userId: String(m.userId),
              userName: m.userName || m.userId
            }))
          }
        }
      } catch (e) {
        console.warn('加载组详情失败', e)
      }
    },
    async searchUsers(keyword) {
      if (!keyword) return
      this.userLoading = true
      try {
        // 关键：把 keyword 作为 likeName 传给后端做模糊搜索(EMAIL/ENGLISH_NAME/CHINESE_NAME)，
        // 否则只拉前 pageSize 个用户在前端过滤，不在该范围内(如排名靠后)的员工永远搜不到。
        const res = await getDropdownUser({ likeName: keyword, pageSize: 50 })
        if (res.ok && res.data) {
          const list = Array.isArray(res.data) ? res.data : (res.data.list || [])
          const kw = keyword.toLowerCase()
          this.userOptions = list
            .filter(u => {
              const name = (u.chineseName || u.userName || u.englishName || '').toLowerCase()
              const id = String(u.userId || '')
              return name.includes(kw) || id.includes(kw)
            })
            .slice(0, 20)
            .map(u => ({
              userId: String(u.userId),
              userName: u.chineseName || u.userName || u.englishName || String(u.userId)
            }))
        }
      } finally {
        this.userLoading = false
      }
    },
    onAddUserSelected(data) {
      if (!data) return
      const userId = data.key === 'userId' ? data.value : (data.value || data.userId)
      if (!userId) return
      this.pendingAddUser = {
        userId: String(userId),
        userName: data.chineseName || data.userName || data.englishName || data.label || String(userId)
      }
    },
    addMember() {
      const { userId, userName } = this.pendingAddUser
      if (!userId) return
      if (this.memberList.some(m => m.userId === userId)) {
        this.$message.warning('该成员已在组内')
        return
      }
      this.memberList.push({ userId, userName })
      this.addUserId = null
      this.pendingAddUser = { userId: '', userName: '' }
    },
    removeMember(index) {
      this.memberList.splice(index, 1)
    },
    buildMembersPayload() {
      if (this.isCreate) {
        return this.selectedUserIds.map(id => {
          const u = this.userOptions.find(o => o.userId === id)
          return { userId: id, userName: u ? u.userName : id }
        })
      }
      return this.memberList.map(m => ({ userId: m.userId, userName: m.userName }))
    },
    async handleSubmit() {
      if (this.isCreate) {
        if (!this.form.stationName) {
          this.$message.warning('请选择所属岗位')
          return
        }
        if (!this.form.groupName) {
          this.$message.warning('请输入组名称')
          return
        }
      } else {
        if (!this.form.groupName) this.form.groupName = this.groupName || ''
        if (!this.form.stationName) this.form.stationName = this.groupStation || ''
        if (!this.form.groupName) {
          this.$message.warning('组信息加载中，请稍后再试')
          return
        }
      }
      const res = await savePersonGroup({
        uuid: this.groupUuid,
        groupName: this.form.groupName,
        stationName: this.form.stationName,
        members: this.buildMembersPayload()
      })
      if (res.ok) {
        this.$message.success('保存成功')
        this.$emit('success')
        this.dialogVisible = false
      }
    }
  }
}
</script>

<style scoped>
.manage-members .group-info {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f5f7fa;
  border-radius: 4px;
}
.manage-members .info-item label {
  color: #909399;
  margin-right: 8px;
}
.member-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
</style>
